import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, CalendarPlus, AlertTriangle } from "lucide-react";
import { apiClient } from "@/App";
import { toast } from "sonner";

const GIG_TYPE_LABELS = {
  rehearsal: "Rehearsal",
  public: "Gig",
  function: "Function",
  session: "Session",
};

// Dates arrive from the parser already as "yyyy-MM-dd" strings and stay strings
// all the way to the API — no Date round-trip, so no UTC shift can move a
// late-night gig back a day.
const rowFromEvent = (ev, i) => ({
  _key: `${ev.date || "nodate"}-${i}`,
  include: !ev.is_duplicate,
  gig_type: ev.gig_type || "public",
  date: ev.date || "",
  title: ev.title || "",
  venue_name: ev.venue_name || "",
  city: ev.city || "",
  end_date: ev.end_date || "",
  start_time: ev.start_time || "",
  call_time: ev.call_time || "",
  finish_time: ev.finish_time || "",
  fee: ev.fee != null ? String(ev.fee) : "",
  is_duplicate: !!ev.is_duplicate,
  same_day: ev.same_day || null,
  // Everything the row doesn't surface for editing still has to reach the API.
  _rest: ev,
});

/**
 * Review step for a multi-date Smart Fill paste.
 *
 * Nothing has been written when this opens — the parser only previewed. Each
 * row the user keeps becomes an ordinary gig via POST /gigs/batch-create,
 * editable afterwards exactly like one typed by hand.
 */
export function SmartFillScheduleReview({ open, onOpenChange, parsed, onCreated }) {
  const [rows, setRows] = useState([]);
  const [shared, setShared] = useState({});
  const [feeMode, setFeeMode] = useState("none");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!parsed) return;
    setRows((parsed.events || []).map(rowFromEvent));
    setShared(parsed.shared || {});
    setFeeMode("none");
  }, [parsed]);

  const setRow = (key, patch) =>
    setRows(prev => prev.map(r => (r._key === key ? { ...r, ...patch } : r)));

  const included = rows.filter(r => r.include);
  const datelessIncluded = included.filter(r => !r.date).length;
  const totalFee = parsed?.shared?.total_fee ?? null;

  // Per-row fee actually sent: an explicit row fee always wins; the engagement
  // fee only fills the blanks, and only in the mode the user picked.
  const resolvedFee = useMemo(() => {
    const map = {};
    const blanks = included.filter(r => !r.fee);
    for (const r of rows) {
      if (r.fee) { map[r._key] = r.fee; continue; }
      if (!r.include || totalFee == null || feeMode === "none") { map[r._key] = ""; continue; }
      map[r._key] = feeMode === "split" && blanks.length
        ? String(Math.round((totalFee / blanks.length) * 100) / 100)
        : String(totalFee);
    }
    return map;
  }, [rows, included, feeMode, totalFee]);

  const handleCreate = async () => {
    if (!included.length) return;
    if (datelessIncluded) {
      toast.error(`${datelessIncluded} selected ${datelessIncluded === 1 ? "row has" : "rows have"} no date — add one or untick.`);
      return;
    }
    setCreating(true);
    try {
      const events = included.map(r => ({
        ...r._rest,
        gig_type: r.gig_type,
        date: r.date,
        end_date: r.end_date || null,
        title: r.title || null,
        venue_name: r.venue_name || null,
        city: r.city || null,
        start_time: r.start_time || null,
        call_time: r.call_time || null,
        finish_time: r.finish_time || null,
        fee: resolvedFee[r._key] || null,
        place_id: r._rest?.venue_candidates?.[0]?.place_id || null,
      }));
      const res = await apiClient.post("/gigs/batch-create", { events, shared });
      const data = res.data || {};
      const made = data.total_created || 0;
      if (made) toast.success(`Created ${made} ${made === 1 ? "event" : "events"}`);
      if (data.skipped?.length) {
        toast.error(`${data.skipped.length} couldn't be created: ${data.skipped.map(s => s.reason).join("; ")}`, { duration: 9000 });
      }
      onOpenChange(false);
      onCreated?.(data);
    } catch (err) {
      // 403 from the free-plan check carries a structured detail.
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "Couldn't create those events.", { duration: 9000 });
    } finally {
      setCreating(false);
    }
  };

  if (!parsed) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="schedule-review-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {rows.length} {rows.length === 1 ? "event" : "events"} found
          </DialogTitle>
          <DialogDescription>
            Check the details, untick anything you don't want, then create them. Each one becomes a normal gig you can edit afterwards.
          </DialogDescription>
        </DialogHeader>

        {parsed.truncated && (
          <div className="flex items-start gap-2 rounded-md border border-border bg-secondary/40 p-2.5 text-xs text-muted-foreground">
            <AlertTriangle className="h-4 w-4 shrink-0 text-primary" />
            <span>That schedule had more dates than we can read at once — only the first {rows.length} are shown. Paste the rest separately.</span>
          </div>
        )}

        {/* Facts stated once for the whole engagement. Editing here fills any
            row that didn't state its own. */}
        {(shared.title || shared.venue_name || shared.city || shared.dress_code) && (
          <div className="rounded-lg border border-border bg-secondary/30 p-3 space-y-2" data-testid="schedule-shared-strip">
            <p className="text-xs text-muted-foreground">Applies to every event unless a row says otherwise:</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {["title", "venue_name", "city", "dress_code"].map(k => (
                <div key={k} className="space-y-1">
                  <Label className="text-xs text-muted-foreground">
                    {{ title: "Band / act", venue_name: "Venue", city: "City", dress_code: "Dress" }[k]}
                  </Label>
                  <Input
                    value={shared[k] || ""}
                    onChange={e => setShared(s => ({ ...s, [k]: e.target.value }))}
                    className="h-8 text-sm"
                    data-testid={`schedule-shared-${k}`}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* One fee stated for the whole run — ask rather than multiplying it by N. */}
        {totalFee != null && (
          <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2" data-testid="schedule-fee-prompt">
            <p className="text-sm">
              One fee of <span className="font-semibold">£{totalFee}</span> was stated for the whole engagement. What should each event get?
            </p>
            <div className="flex flex-wrap gap-2">
              {[
                ["none", "Leave blank"],
                ["split", `Split evenly${included.filter(r => !r.fee).length ? ` (£${Math.round((totalFee / included.filter(r => !r.fee).length) * 100) / 100} each)` : ""}`],
                ["each", `Apply £${totalFee} to each`],
              ].map(([mode, label]) => (
                <Button
                  key={mode}
                  type="button"
                  size="sm"
                  variant={feeMode === mode ? "default" : "outline"}
                  onClick={() => setFeeMode(mode)}
                  data-testid={`schedule-fee-${mode}`}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-2">
          {rows.map(r => (
            <div
              key={r._key}
              className={`rounded-lg border p-3 transition-colors ${r.include ? "border-border bg-card" : "border-dashed border-border bg-transparent opacity-60"}`}
              data-testid="schedule-review-row"
            >
              <div className="flex items-start gap-3">
                <Checkbox
                  checked={r.include}
                  onCheckedChange={v => setRow(r._key, { include: !!v })}
                  className="mt-1"
                  data-testid={`schedule-row-include-${r._key}`}
                />
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Select value={r.gig_type} onValueChange={v => setRow(r._key, { gig_type: v })}>
                      <SelectTrigger className="h-7 w-[130px] text-xs" data-testid={`schedule-row-type-${r._key}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(GIG_TYPE_LABELS).map(([v, l]) => (
                          <SelectItem key={v} value={v}>{l}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {r.is_duplicate && (
                      <Badge variant="outline" className="text-xs">Already in your diary</Badge>
                    )}
                    {/* Same date, different venue — a clash worth seeing, but an
                        afternoon rehearsal and an evening gig are both real, so
                        this stays ticked rather than being treated as a dupe. */}
                    {!r.is_duplicate && r.same_day && (
                      <Badge variant="secondary" className="text-xs" data-testid={`schedule-row-clash-${r._key}`}>
                        Also that day: {r.same_day.label}
                        {r.same_day.count > 1 ? ` +${r.same_day.count - 1} more` : ""}
                      </Badge>
                    )}
                    {!r.date && (
                      <Badge variant="destructive" className="text-xs">Needs a date</Badge>
                    )}
                    {/* A date range the parser found. Shown so a multi-day row
                        can't carry an invisible end_date into the created gig. */}
                    {r.end_date && (
                      <Badge variant="outline" className="text-xs" data-testid={`schedule-row-span-${r._key}`}>
                        Runs to {r.end_date.split("-").reverse().slice(0, 2).join("/")}
                      </Badge>
                    )}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {/* Date spans the full width on mobile: a UK date input's
                        text + picker glyph needs ~139px and a half-width cell
                        gives ~126px, which clips the glyph. */}
                    <div className="space-y-1 col-span-2 md:col-span-1">
                      <Label className="text-xs text-muted-foreground">Date</Label>
                      {/* min-w-0 + appearance-none: iOS ignores width on native
                          date/time inputs and overflows the row otherwise. */}
                      <Input
                        type="date"
                        value={r.date}
                        onChange={e => setRow(r._key, { date: e.target.value })}
                        className="h-8 text-sm min-w-0 appearance-none max-w-full"
                        data-testid={`schedule-row-date-${r._key}`}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Start</Label>
                      <Input
                        type="time"
                        value={r.start_time}
                        onChange={e => setRow(r._key, { start_time: e.target.value })}
                        className="h-8 text-sm min-w-0 appearance-none max-w-full"
                        data-testid={`schedule-row-start-${r._key}`}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">End</Label>
                      <Input
                        type="time"
                        value={r.finish_time}
                        onChange={e => setRow(r._key, { finish_time: e.target.value })}
                        className="h-8 text-sm min-w-0 appearance-none max-w-full"
                        data-testid={`schedule-row-end-${r._key}`}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Call</Label>
                      <Input
                        type="time"
                        value={r.call_time}
                        onChange={e => setRow(r._key, { call_time: e.target.value })}
                        className="h-8 text-sm min-w-0 appearance-none max-w-full"
                        data-testid={`schedule-row-call-${r._key}`}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Band / act</Label>
                      <Input
                        value={r.title}
                        placeholder={shared.title || ""}
                        onChange={e => setRow(r._key, { title: e.target.value })}
                        className="h-8 text-sm"
                        data-testid={`schedule-row-title-${r._key}`}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Venue</Label>
                      <Input
                        value={r.venue_name}
                        placeholder={shared.venue_name || ""}
                        onChange={e => setRow(r._key, { venue_name: e.target.value })}
                        className="h-8 text-sm"
                        data-testid={`schedule-row-venue-${r._key}`}
                      />
                    </div>
                    <div className="space-y-1 col-span-2 md:col-span-1">
                      <Label className="text-xs text-muted-foreground">Fee (£)</Label>
                      <Input
                        inputMode="decimal"
                        value={r.fee}
                        placeholder={resolvedFee[r._key] || ""}
                        onChange={e => setRow(r._key, { fee: e.target.value.replace(/[^0-9.]/g, "") })}
                        className="h-8 text-sm"
                        data-testid={`schedule-row-fee-${r._key}`}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={creating}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleCreate}
            disabled={creating || !included.length}
            className="bg-primary text-primary-foreground"
            data-testid="schedule-create-btn"
          >
            {creating ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating...</>
            ) : (
              <><CalendarPlus className="h-4 w-4 mr-2" />Create {included.length} of {rows.length}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SmartFillScheduleReview;
